import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useInfiniteScrollSentinel } from '~/ui-rocco/pages/category-page/hooks/useInfiniteScrollSentinel';

interface EditorialViewMoreProps {
    hasMore: boolean;
    isLoading: boolean;
    onLoadMore: () => void;
    /**
     * The `?page=N` the next step lands on, so the control can be a real anchor
     * a crawler can follow.
     */
    nextPage: number;
    className?: string;
}

/**
 * The editorial grid's paging control.
 *
 * The same hybrid the directory browse grids run: page one shows an explicit
 * Load More and nothing auto-loads, the first click takes the reader to page two
 * and switches infinite scroll on for the rest of the session, and a new result
 * set puts the button back. Mirrors DirectoryUniformGrid rather than inventing a
 * second behaviour for articles.
 *
 * The control is an anchor at a real `?page=N`, not a button: a crawler that
 * runs no JavaScript still has a path through the whole listing. Plain
 * left-clicks are intercepted and served client-side; modified clicks are left
 * alone so open-in-new-tab lands on the real URL.
 */
export const EditorialViewMore = ({
    hasMore,
    isLoading,
    onLoadMore,
    nextPage,
    className,
}: EditorialViewMoreProps) => {
    const location = useLocation();

    // Infinite scroll stays off until Load More is taken once. It starts off on
    // every fresh result set — SSR and hydration render the crawlable anchor,
    // not the sentinel.
    const [infiniteEnabled, setInfiniteEnabled] = useState(false);

    // Result-set identity: the listing and its facets, with `?page=N` dropped.
    // Paging rewrites that param without changing the router location, so this
    // stays stable through infinite scroll and only moves on a real facet,
    // order or category change.
    const resultSetKey = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.delete('page');
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search]);

    useEffect(() => {
        setInfiniteEnabled(false);
    }, [resultSetKey]);

    // One scroll after arming, before the sentinel may fire. Without it a click
    // that lands near the end of the page can trip the sentinel immediately and
    // pull a second page the reader never asked for.
    const [infiniteArmed, setInfiniteArmed] = useState(false);
    useEffect(() => {
        if (!infiniteEnabled) {
            setInfiniteArmed(false);
            return;
        }
        const arm = () => setInfiniteArmed(true);
        window.addEventListener('scroll', arm, { passive: true, once: true });
        return () => window.removeEventListener('scroll', arm);
    }, [infiniteEnabled]);

    const handleLoadMore = useCallback(() => {
        setInfiniteEnabled(true);
        onLoadMore();
    }, [onLoadMore]);

    const handleAnchorClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }
            event.preventDefault();
            handleLoadMore();
        },
        [handleLoadMore],
    );

    const sentinelRef = useInfiniteScrollSentinel({
        enabled: infiniteEnabled && infiniteArmed && hasMore && !isLoading,
        onTrigger: onLoadMore,
    });

    const nextPageHref = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.set('page', String(nextPage));
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search, nextPage]);

    if (!hasMore) return null;

    return (
        <div className={cn('flex flex-col items-center', className)}>
            {!infiniteEnabled && (
                // The platform's Load More, the same one the detail pages and
                // the profile carry. It keeps the crawlable `?page=` address
                // this control has always had — the editorial grids are
                // paginated for search as well as for readers.
                <LoadMoreButton
                    href={nextPageHref}
                    onClick={handleAnchorClick}
                    isLoading={isLoading}
                />
            )}

            {/*
             * Sits below the last row so it enters the viewport as the reader
             * reaches the end. Disarmed until Load More has been taken, so the
             * page never grows unasked.
             */}
            <div ref={sentinelRef} aria-hidden={true} className="h-px w-full" />

            <span aria-live="polite" className="sr-only">
                {isLoading ? 'Loading more articles' : ''}
            </span>
        </div>
    );
};
